/**
 * Blended stock sentiment — the "what's the read" signal traders want. Combines Polygon's per-article
 * news sentiment (editorial lean) with today's price momentum (the market voting with dollars) and the
 * community's bull/bear votes. Pure + deterministic (takes plain counts, no DB/type deps) so it runs
 * on server AND client. Score is 0–100 (0 = max bearish, 100 = max bullish); 40–60 reads as mixed.
 */

export type Sentiment = {
  score: number            // 0..100
  label: 'Bullish' | 'Bearish' | 'Neutral'
  bullNews: number
  bearNews: number
  neutralNews: number
  dayPct: number | null
  bullVotes: number        // community
  bearVotes: number        // community
  hasSignal: boolean       // false when there's nothing to base a read on
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n))

export function computeSentiment(opts: {
  bullNews?: number
  bearNews?: number
  neutralNews?: number
  dayPct?: number | null
  bullVotes?: number
  bearVotes?: number
}): Sentiment {
  const bullNews = opts.bullNews ?? 0
  const bearNews = opts.bearNews ?? 0
  const neutralNews = opts.neutralNews ?? 0
  const bullVotes = opts.bullVotes ?? 0
  const bearVotes = opts.bearVotes ?? 0
  const dayPct = opts.dayPct ?? null

  // Each sub-signal is a lean in [-1, +1]; blend by weight, skipping absent signals.
  const parts: { lean: number; weight: number }[] = []
  if (bullNews + bearNews > 0) parts.push({ lean: (bullNews - bearNews) / (bullNews + bearNews), weight: 0.45 })
  if (dayPct != null) parts.push({ lean: clamp(dayPct / 8, -1, 1), weight: 0.35 }) // ±8% day move ≈ full lean
  if (bullVotes + bearVotes > 0) parts.push({ lean: (bullVotes - bearVotes) / (bullVotes + bearVotes), weight: 0.5 })

  const hasSignal = parts.length > 0
  const wSum = parts.reduce((s, p) => s + p.weight, 0) || 1
  const lean = parts.reduce((s, p) => s + p.lean * p.weight, 0) / wSum // -1..+1
  const score = Math.round((lean + 1) * 50)
  const label: Sentiment['label'] = score >= 60 ? 'Bullish' : score <= 40 ? 'Bearish' : 'Neutral'

  return { score, label, bullNews, bearNews, neutralNews, dayPct, bullVotes, bearVotes, hasSignal }
}
